import type { PersonalisationErrorType } from 'shop/api/personalisations.api'

// Named "personalisation transfer": TransfersModal.tsx already means money.
// Never route translated copy through EloInput hintText/errorText (dangerouslySetInnerHTML).

export const PERSONALISATION_TRANSFER_COPY = {
  get action(): string {
    return I18n.t('react.shared.personalisation.personalisation_transfer_action')
  },
  get modalTitle(): string {
    return I18n.t('react.shared.personalisation.personalisation_transfer_modal_title')
  },
  get ticketPrefix(): string {
    return I18n.t('react.shared.personalisation.personalisation_transfer_ticket_prefix')
  },
  get holderPrefix(): string {
    return I18n.t('react.shared.personalisation.personalisation_transfer_holder_prefix')
  },
  get emailLabel(): string {
    return I18n.t('react.shared.personalisation.personalisation_transfer_email_label')
  },
  get emailPlaceholder(): string {
    return I18n.t('react.shared.personalisation.personalisation_transfer_email_placeholder')
  },
  get emailConfirmationLabel(): string {
    return I18n.t('react.shared.personalisation.personalisation_transfer_email_confirmation_label')
  },
  get emailConfirmationPlaceholder(): string {
    return I18n.t('react.shared.personalisation.personalisation_transfer_email_confirmation_placeholder')
  },
  get typeItHint(): string {
    return I18n.t('react.shared.personalisation.personalisation_transfer_type_it_hint')
  },
  get pasteBlocked(): string {
    return I18n.t('react.shared.personalisation.personalisation_transfer_paste_blocked')
  },
  get codeLabel(): string {
    return I18n.t('react.shared.personalisation.personalisation_transfer_code_label')
  },
  get codeHint(): string {
    return I18n.t('react.shared.personalisation.personalisation_transfer_code_hint')
  },
  // The warning's opening clause is the sentence the holder must not skim, so it is bold in
  // the modal. The announced text stays one sentence: `warning` joins both halves.
  get warningLead(): string {
    return I18n.t('react.shared.personalisation.personalisation_transfer_warning_lead')
  },
  get warningRest(): string {
    return I18n.t('react.shared.personalisation.personalisation_transfer_warning_rest')
  },
  get warning(): string {
    return `${PERSONALISATION_TRANSFER_COPY.warningLead} ${PERSONALISATION_TRANSFER_COPY.warningRest}`
  },
  get mismatch(): string {
    return I18n.t('react.shared.personalisation.personalisation_transfer_mismatch')
  },
  get invalidEmail(): string {
    return I18n.t('react.shared.personalisation.personalisation_transfer_invalid_email')
  },
  get sameHolder(): string {
    return I18n.t('react.shared.personalisation.personalisation_transfer_same_holder')
  },
  get confirm(): string {
    return I18n.t('react.shared.personalisation.personalisation_transfer_confirm')
  },
  get confirmBusy(): string {
    return I18n.t('react.shared.personalisation.personalisation_transfer_confirm_busy')
  },
  get slowRequest(): string {
    return I18n.t('react.shared.personalisation.personalisation_transfer_slow_request')
  },
  get cancel(): string {
    return I18n.t('react.shared.button.cancel')
  },
} as const

export const PERSONALISATION_TRANSFER_SUCCESS_TITLE_KEY =
  'react.shared.personalisation.personalisation_transfer_success_title'

export const personalisationTransferSuccessTitle = (email: string): string =>
  I18n.t(PERSONALISATION_TRANSFER_SUCCESS_TITLE_KEY, { email })

export const PERSONALISATION_TRANSFER_RETURN_TO_SHOP_KEY =
  'react.shared.personalisation.personalisation_transfer_return_to_shop'

export const personalisationTransferReturnToShop = (shop: string): string =>
  I18n.t(PERSONALISATION_TRANSFER_RETURN_TO_SHOP_KEY, { shop })

export const PERSONALISATION_TRANSFER_SUCCESS_BODY_KEY =
  'react.shared.personalisation.personalisation_transfer_success_body'

export const personalisationTransferSuccessBody = (): string => I18n.t(PERSONALISATION_TRANSFER_SUCCESS_BODY_KEY)

export const PERSONALISATION_TRANSFER_REFUSAL_KEYS: Partial<Record<PersonalisationErrorType, string>> = {
  invalid_email: 'react.shared.personalisation.personalisation_transfer_refusals_invalid_email',
  email_mismatch: 'react.shared.personalisation.personalisation_transfer_refusals_email_mismatch',
  same_holder: 'react.shared.personalisation.personalisation_transfer_refusals_same_holder',
  not_transferable_status: 'react.shared.personalisation.personalisation_transfer_refusals_not_transferable_status',
  not_transferable_state: 'react.shared.personalisation.personalisation_transfer_refusals_not_transferable_state',
  read_only: 'react.shared.personalisation.personalisation_transfer_refusals_read_only',
  conflict: 'react.shared.personalisation.personalisation_transfer_refusals_conflict',
  invalid_code: 'react.shared.personalisation.personalisation_transfer_refusals_invalid_code',
  rate_limited: 'react.shared.personalisation.personalisation_transfer_refusals_rate_limited',
  not_verified: 'react.shared.personalisation.personalisation_transfer_refusals_not_verified',
  not_found: 'react.shared.personalisation.personalisation_transfer_refusals_not_found',
  temporarily_closed: 'react.shared.personalisation.personalisation_transfer_refusals_temporarily_closed',
  captcha_failed: 'react.shared.personalisation.personalisation_transfer_refusals_captcha_failed',
}

export const PERSONALISATION_TRANSFER_FALLBACK_REFUSAL_KEY =
  'react.shared.personalisation.personalisation_transfer_refusals_fallback'

export const personalisationTransferFallbackRefusal = (): string =>
  I18n.t(PERSONALISATION_TRANSFER_FALLBACK_REFUSAL_KEY)

// `meta.type` is unvalidated. Prototype keys like `constructor` would resolve to a function.
export const personalisationTransferRefusal = (type: string): string => {
  const key = Object.prototype.hasOwnProperty.call(PERSONALISATION_TRANSFER_REFUSAL_KEYS, type)
    ? PERSONALISATION_TRANSFER_REFUSAL_KEYS[type as PersonalisationErrorType]
    : undefined

  return I18n.t(typeof key === 'string' ? key : PERSONALISATION_TRANSFER_FALLBACK_REFUSAL_KEY)
}

export const PERSONALISATION_TRANSFER_HISTORY_LINE_KEY =
  'react.shared.personalisation.personalisation_transfer_history_line'

export const personalisationTransferHistoryLine = (email: string, formattedAt: string): string =>
  I18n.t(PERSONALISATION_TRANSFER_HISTORY_LINE_KEY, { email, formattedAt })

export const PERSONALISATION_TRANSFER_HISTORY_TIME_FORMAT = 'HH:mm, D MMMM YYYY'

// Status names come from react.shop.personalisation.status.*, not from here.
export const PERSONALISATION_TRACKING_FILTER_COPY = {
  get label(): string {
    return I18n.t('react.shared.personalisation.personalisation_transfer_filter_label')
  },
  get allStatuses(): string {
    return I18n.t('react.shared.personalisation.personalisation_transfer_filter_all_statuses')
  },
  get noMatches(): string {
    return I18n.t('react.shared.personalisation.personalisation_transfer_filter_no_matches')
  },
  hiddenStagedEdits: (count: number): string =>
    count === 1
      ? I18n.t('react.shared.personalisation.personalisation_transfer_filter_hidden_staged_edits1', { count })
      : I18n.t('react.shared.personalisation.personalisation_transfer_filter_hidden_staged_edits0', { count }),
} as const

export const PERSONALISATION_TRANSFER_SLOW_MS = 12000
